import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { User } from '../data/mockData';
import { createMeasurement } from '../services/measurementService';
import MeasurementNameInput from '../components/MeasurementForm/MeasurementNameInput';
import AdvertiserInput from '../components/MeasurementForm/AdvertiserInput';
import BrandInput from '../components/MeasurementForm/BrandInput';
import PendingCreativesField from '../components/MeasurementForm/PendingCreativesField';
import EndOfCampaignReportInput from '../components/MeasurementForm/EndOfCampaignReportInput';
import { usePendingCreatives } from '../hooks/usePendingCreatives';
import './CreateMeasurementPage.css';

const CreateMeasurementPage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [name, setName] = useState('');
  const [advertiser, setAdvertiser] = useState('');
  const [brand, setBrand] = useState<string | null>(null);
  const [endOfCampaignReport, setEndOfCampaignReport] = useState<File | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string>('');
  const {
    creatives,
    error: fileError,
    addCreative,
    removeCreative,
    canSubmit,
  } = usePendingCreatives();
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const displayError = error || fileError;

  const handleEndOfCampaignReportChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const fileName = file.name.toLowerCase();
      const isValidType = fileName.endsWith('.xlsx') ||
                         fileName.endsWith('.xls') ||
                         fileName.endsWith('.csv');
      if (!isValidType) {
        setError('End of campaign report must be an Excel (.xlsx, .xls) or CSV file');
        e.target.value = '';
        return;
      }
      setEndOfCampaignReport(file);
      setError('');
    } else {
      setEndOfCampaignReport(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Please enter a measurement name');
      return;
    }

    if (!advertiser.trim()) {
      setError('Please enter an advertiser');
      return;
    }

    if (!endOfCampaignReport) {
      setError('Please upload an End of campaign report file');
      return;
    }

    if (creatives.length === 0) {
      setError('Please add at least one creative');
      return;
    }

    if (!canSubmit) {
      setError('Please enter an external ID for all creatives');
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      const result = await createMeasurement(
        name,
        advertiser,
        brand || null,
        creatives,
        endOfCampaignReport
      );

      setIsCreating(false);

      if (result.acceptedAsync) {
        navigate('/measurements');
        return;
      }

      navigate(`/measurements/${result.measurementId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create measurement. Please try again.');
      setIsCreating(false);
    }
  };

  if (!user) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <Layout user={user}>
      <div className="create-measurement-page">
        <h1>Create Measurement</h1>

        <form onSubmit={handleSubmit} className="measurement-form">
          <MeasurementNameInput
            value={name}
            onChange={setName}
            disabled={isCreating}
          />

          <AdvertiserInput
            value={advertiser}
            onChange={setAdvertiser}
            disabled={isCreating}
          />

          <BrandInput
            value={brand}
            onChange={setBrand}
            disabled={isCreating}
          />

          <EndOfCampaignReportInput
            file={endOfCampaignReport}
            onChange={handleEndOfCampaignReportChange}
            disabled={isCreating}
          />

          <PendingCreativesField
            creatives={creatives}
            onAdd={addCreative}
            onRemove={removeCreative}
            disabled={isCreating}
          />

          {displayError && (
            <div className="error-message">
              {displayError}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/measurements')}
              className="cancel-button"
              disabled={isCreating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="create-button"
              disabled={
                isCreating ||
                !name.trim() ||
                !advertiser.trim() ||
                !endOfCampaignReport ||
                creatives.length === 0 ||
                !canSubmit
              }
            >
              {isCreating ? 'Uploading…' : 'Create Measurement'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreateMeasurementPage;
